import { useState, useCallback, useEffect, useRef } from 'preact/hooks';
import { BaseDialog, BtnCancel } from '@components/dialog';
import { BtnPrimary } from '@components/buttons';
import { useIntl } from 'shared/intl/use-intl';
import { ImagePreview } from './image-preview';
import { ImageEditor } from './image-editor';
import { makeUploader } from 'client/lib/rpx-client';
import { FilePickerResult } from './index';

type UploadStatus = 'pending' | 'editing' | 'uploading' | 'success' | 'failed';

type UploadableImageImage = {
  id: string;
  file: File;
  previewUrl: string;
  status: UploadStatus;
  progress: number;
  error?: string;
  result?: FilePickerResult;
};

type MultiImagePreviewModalProps = {
  files: File[] | FileList;
  onCancel: () => void;
  onComplete: (results: FilePickerResult[]) => void;
  isPublic?: boolean;
};

type ImageUploaderProps = {
  file: File;
  isPublic?: boolean;
  onProgress: (progress: number) => void;
  onSuccess: (result: FilePickerResult) => void;
  onFailure: (error: Error) => void;
};

function ImageUploader({ file, isPublic, onProgress, onSuccess, onFailure }: ImageUploaderProps) {
  useEffect(() => {
    const uploader = makeUploader({
      file,
      isPublic,
      onProgress: ({ progress }) => onProgress(progress),
    });

    uploader
      .upload()
      .then((result) => {
        if (result) {
          onSuccess(result);
        } else {
          onFailure(new Error('Upload failed: no result returned'));
        }
      })
      .catch(onFailure);
  }, [file, isPublic, onProgress, onSuccess, onFailure]);

  return null;
}

export function MultiImagePreviewModal({
  files,
  onCancel,
  onComplete,
  isPublic,
}: MultiImagePreviewModalProps) {
  const intl = useIntl();
  const filesArray = Array.isArray(files) ? files : Array.from(files);
  const imageRefs = useRef(new Map<string, HTMLDivElement>());

  const [images, setImages] = useState<UploadableImageImage[]>(() =>
    filesArray.map((file, index) => ({
      file,
      previewUrl: URL.createObjectURL(file),
      id: `img-${index}`,
      status: 'pending',
      progress: 0,
    })),
  );

  const [isUploading, setIsUploading] = useState(false);
  const [activeUploadIndex, setActiveUploadIndex] = useState(0);
  const [selectedImageId, setSelectedImageId] = useState<string | null>(null);

  useEffect(() => {
    if (isUploading && activeUploadIndex < images.length) {
      const activeImage = images[activeUploadIndex];
      imageRefs.current
        .get(activeImage.id)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [activeUploadIndex, isUploading, images]);

  const handleStartUpload = useCallback(() => {
    setActiveUploadIndex(0);
    setIsUploading(true);
  }, []);
  //If a single file was selected, the quikpik default ImageEditor was shown to user
  //Therefore we start the upload immediately without showing the preview modal
  useEffect(() => {
    if (filesArray.length === 1) {
      handleStartUpload();
    }
  }, [filesArray, handleStartUpload]);

  const handleNext = useCallback(() => {
    setActiveUploadIndex((prev) => prev + 1);
  }, []);

  const handleImageUploadSuccess = useCallback(
    (imageId: string, result: FilePickerResult) => {
      setImages((prev) =>
        prev.map((it) =>
          it.id === imageId ? { ...it, status: 'success', progress: 100, result } : it,
        ),
      );
      handleNext();
    },
    [handleNext],
  );

  const handleImageUploadFailure = useCallback(
    (imageId: string, error: Error) => {
      setImages((prev) =>
        prev.map((it) =>
          it.id === imageId ? { ...it, status: 'failed', error: error.message } : it,
        ),
      );
      handleNext();
    },
    [handleNext],
  );

  const handleRetryUpload = useCallback(
    async (imageId: string) => {
      const imageToRetry = images.find((it) => it.id === imageId);
      if (!imageToRetry) {
        return;
      }
      setImages((prev) =>
        prev.map((it) =>
          it.id === imageId ? { ...it, status: 'uploading', progress: 0, error: undefined } : it,
        ),
      );

      try {
        const uploader = makeUploader({
          file: imageToRetry.file,
          isPublic,
          onProgress: ({ progress }) => {
            setImages((prev) => prev.map((it) => (it.id === imageId ? { ...it, progress } : it)));
          },
        });
        const result = await uploader.upload();
        if (!result) {
          throw new Error('Upload failed to return a result.');
        }
        setImages((prev) =>
          prev.map((it) =>
            it.id === imageId ? { ...it, status: 'success', result, progress: 100 } : it,
          ),
        );
      } catch (error: any) {
        setImages((prev) =>
          prev.map((it) =>
            it.id === imageId ? { ...it, status: 'failed', error: error.message } : it,
          ),
        );
      }
    },
    [images, isPublic],
  );

  const handleDone = useCallback(() => {
    const successfulResults = images
      .filter((image) => image.status === 'success')
      .map((image) => image.result!);
    onComplete(successfulResults);
  }, [images, onComplete]);

  const handleImageEditorClose = useCallback(
    (editedFile?: File) => {
      if (editedFile && selectedImageId) {
        setImages((currentImages) => {
          const oldImage = currentImages.find((image) => image.id === selectedImageId);
          if (oldImage) {
            URL.revokeObjectURL(oldImage.previewUrl);
          }
          return currentImages.map((image) =>
            image.id === selectedImageId
              ? { ...image, file: editedFile, previewUrl: URL.createObjectURL(editedFile) }
              : image,
          );
        });
      }
      setSelectedImageId(null);
    },
    [selectedImageId],
  );

  useEffect(() => {
    return () => {
      images.forEach((image) => URL.revokeObjectURL(image.previewUrl));
    };
  }, []);

  const activeImage =
    isUploading && activeUploadIndex < images.length ? images[activeUploadIndex] : null;
  const totalToUpload = images.length;
  const isFinished = isUploading && activeUploadIndex >= totalToUpload;
  const successfulCount = images.filter((it) => it.status === 'success').length;
  const selectedImage = images.find((image) => image.id === selectedImageId);

  useEffect(() => {
    if (isFinished) {
      handleDone();
    }
  }, [isFinished, handleDone]);

  if (selectedImageId && selectedImage) {
    return (
      <ImageEditor
        file={selectedImage.file}
        previewUrl={selectedImage.previewUrl}
        onCancel={() => handleImageEditorClose()}
        onConfirm={handleImageEditorClose}
      />
    );
  }

  return (
    <BaseDialog onClose={isUploading && !isFinished ? () => {} : onCancel} blurBg={false}>
      {activeImage && activeImage.status === 'pending' && (
        <ImageUploader
          file={activeImage.file}
          isPublic={isPublic}
          onProgress={(progress) =>
            setImages((prev) =>
              prev.map((image) =>
                image.id === activeImage.id ? { ...image, status: 'uploading', progress } : image,
              ),
            )
          }
          onSuccess={(result) => handleImageUploadSuccess(activeImage.id, result)}
          onFailure={(error) => handleImageUploadFailure(activeImage.id, error)}
        />
      )}
      <div class="w-full max-w-2xl flex flex-col max-h-[90vh] overflow-hidden rounded-lg bg-white">
        <header class="p-6 pb-4 border-b border-gray-200 flex-shrink-0">
          <h2 class="font-semibold text-lg">
            {isUploading &&
              !isFinished &&
              `Uploading ${activeUploadIndex + 1} of ${totalToUpload}...`}
            {isFinished && 'Upload Complete'}
            {!isUploading && 'Preview Selected Images'}
          </h2>
          <p class="text-gray-600 text-sm mt-1">
            {isFinished
              ? `${successfulCount} of ${totalToUpload} files uploaded.`
              : isUploading
                ? 'Please wait while the images are being uploaded'
                : 'Review your images and make edits if needed before uploading'}
          </p>
        </header>
        <div class="flex-grow p-6 min-h-0 overflow-y-auto">
          <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {images.map((image) => (
              <div
                ref={(el) => {
                  if (el) {
                    imageRefs.current.set(image.id, el);
                  }
                }}
                key={image.id}
              >
                <ImagePreview
                  file={image.file}
                  previewUrl={image.previewUrl}
                  status={image.status}
                  progress={image.progress}
                  onEdit={() => setSelectedImageId(image.id)}
                  onRetry={() => handleRetryUpload(image.id)}
                  disabled={isUploading}
                />
              </div>
            ))}
          </div>
        </div>

        <footer class="p-6 pt-4 border-t border-gray-200 flex-shrink-0 flex items-center gap-3">
          {!isUploading && (
            <>
              <BtnPrimary class="rounded-md" onClick={handleStartUpload}>
                {intl('Upload All Images')} ({images.length})
              </BtnPrimary>
              <BtnCancel
                class="px-4 py-2 rounded-md border bg-white border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-medium transition-colors disabled:opacity-50"
                onClick={onCancel}
              >
                {intl('Cancel')}
              </BtnCancel>
            </>
          )}
          {isFinished && (
            <BtnPrimary class="rounded-md" onClick={handleDone}>
              {intl('Done')}
            </BtnPrimary>
          )}
        </footer>
      </div>
    </BaseDialog>
  );
}
